import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { X } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer } from "recharts";
import { MapContainer, TileLayer, Polyline, CircleMarker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { fetcher } from "@/lib/api";
import { VehicleSilhouette, typeStyle } from "./VehicleSilhouette";
import { Button } from "@/components/ui/button";

export default function FrotaDetailDrawer({ plate, open, onClose }) {
  const { t } = useTranslation();
  const { data: detail } = useSWR(open && plate ? `/frota/${plate}` : null, fetcher);
  const { data: spark } = useSWR(
    open && plate ? `/frota/${plate}/carbon-sparkline?days=30` : null,
    fetcher
  );

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const v = detail?.vehicle;
  const ts = v ? typeStyle(v.vehicle_type) : typeStyle("outro");
  const trajectory = (detail?.positions_24h || [])
    .filter((p) => p.latitude != null && p.longitude != null)
    .map((p) => [p.latitude, p.longitude]);

  return (
    <div
      className="fixed inset-0 z-[500] flex justify-end"
      role="dialog"
      aria-modal="true"
      data-testid="frota-detail-drawer"
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside className="relative w-full max-w-[560px] h-full bg-salnes-ivory overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className={`${ts.bg} ${ts.fg} p-6 flex items-start gap-4`}>
          <VehicleSilhouette type={v?.vehicle_type || "outro"} className="w-24 h-16 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.24em] opacity-80">
              {v?.plate}
            </div>
            <h2 className="font-serif text-2xl leading-tight truncate">
              {v?.brand} {v?.model}
            </h2>
            <div className="text-xs opacity-80 mt-1">
              {v && t(`fase8.frota.type_${v.vehicle_type || "outro"}`)}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white"
            data-testid="drawer-close"
            aria-label={t("fase8.frota.close")}
          >
            <X size={20} />
          </button>
        </div>

        {!v ? (
          <div className="p-6 text-sm text-salnes-stone">Loading…</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Ficha técnica grid */}
            <section>
              <h3 className="font-serif text-lg text-salnes-ink mb-3">
                {t("fase8.frota.detail_title")}
              </h3>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-xs">
                <FieldRow label={t("fase8.frota.detail_matriculacion")} value={v.matriculacion} />
                <FieldRow label={t("fase8.frota.detail_categoria")} value={v.categoria} />
                <FieldRow label={t("fase8.frota.card_euro")} value={v.euro_emission_class} />
                <FieldRow label={t("fase8.frota.detail_etiqueta")} value={v.etiqueta_dgt} />
                <FieldRow label={t("fase8.frota.card_fuel")} value={t(`fase8.frota.fuel_${v.fuel_type}`, v.fuel_type)} />
                <FieldRow label={t("fase8.frota.detail_km_dia")} value={v.km_dia_avg} />
                <FieldRow
                  label={t("fase8.frota.detail_annual_km")}
                  value={v.annual_km_estimated?.toLocaleString?.()}
                  provisional={v.provisional_annual_km}
                />
                <FieldRow
                  label={t("fase8.frota.detail_consumption")}
                  value={v.avg_consumption_l_per_km}
                  provisional={v.provisional_consumption}
                />
                <FieldRow label={t("fase8.frota.detail_centro")} value={v.centro} span={2} />
                <FieldRow label={t("fase8.frota.detail_grupo")} value={v.grupo} span={2} />
              </dl>
            </section>

            {/* Activity sparkline */}
            <section>
              <h3 className="font-serif text-lg text-salnes-ink mb-2">
                {t("fase8.frota.detail_activity_series")}
              </h3>
              {spark?.series?.length ? (
                <div className="h-32 -mx-2">
                  <ResponsiveContainer>
                    <LineChart data={spark.series}>
                      <XAxis dataKey="date" tick={{ fontSize: 9 }} interval="preserveStartEnd" hide={spark.series.length > 15} />
                      <YAxis tick={{ fontSize: 9 }} />
                      <ReTooltip
                        contentStyle={{ fontSize: 11, borderRadius: 8 }}
                        formatter={(val, key) => [Number(val).toFixed(1), key === "kgco2e" ? "kg CO₂e" : "km"]}
                      />
                      <Line type="monotone" dataKey="kgco2e" stroke="#10B981" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="km" stroke="#0B4C7A" strokeWidth={1.5} dot={false} strokeDasharray="3 3" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-xs text-salnes-stone italic">{t("fase8.frota.detail_no_gps")}</p>
              )}
              {spark?.totals && (
                <div className="mt-2 flex gap-4 text-[11px] text-salnes-stone">
                  <span>
                    <b className="text-salnes-ink">{spark.totals.km.toLocaleString()}</b> km
                  </span>
                  <span>
                    <b className="text-salnes-ink">{spark.totals.kgco2e.toLocaleString()}</b> kg CO₂e
                  </span>
                  {spark.provisional && (
                    <span className="ml-auto text-salnes-coral font-medium uppercase tracking-[0.12em] text-[9px]">
                      {t("fase8.frota.detail_provisional")}
                    </span>
                  )}
                </div>
              )}
            </section>

            {/* Trajectory map (only if GPS) */}
            {trajectory.length >= 2 && (
              <section>
                <h3 className="font-serif text-lg text-salnes-ink mb-2">
                  {t("fase8.frota.detail_trajectory")}
                </h3>
                <div className="h-56 rounded-xl overflow-hidden border hairline">
                  <MapContainer
                    center={trajectory[trajectory.length - 1]}
                    zoom={13}
                    scrollWheelZoom={false}
                    style={{ height: "100%", width: "100%" }}
                  >
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                      attribution='&copy; OpenStreetMap · CARTO'
                    />
                    <Polyline positions={trajectory} pathOptions={{ color: "#0B4C7A", weight: 3, opacity: 0.85 }} />
                    <CircleMarker
                      center={trajectory[trajectory.length - 1]}
                      radius={7}
                      pathOptions={{ color: "#10B981", fillColor: "#10B981", fillOpacity: 1 }}
                    />
                  </MapContainer>
                </div>
                <div className="mt-2 text-[11px] text-salnes-stone">
                  {t("fase8.frota.detail_km_24h")}:{" "}
                  <b className="text-salnes-ink">{detail?.km_24h?.toLocaleString?.() || 0}</b> km
                </div>
              </section>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

function FieldRow({ label, value, provisional, span = 1 }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <dt className="text-[10px] uppercase tracking-[0.18em] text-salnes-stone">{label}</dt>
      <dd className="text-salnes-ink font-medium flex items-center gap-2">
        <span>{value ?? "—"}</span>
        {provisional && (
          <span className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-salnes-coral/10 text-salnes-coral">
            prov.
          </span>
        )}
      </dd>
    </div>
  );
}
